/* Store-owned regional promotion bridge for Shopify Dawn. */
(() => {
  'use strict';
  const config = window.PFMRegionalOffersConfig;
  if (!config || !Number(config.autoApplyProductId) || window.PFMRegionalOffers) return;
  const productId = Number(config.autoApplyProductId);
  const promoCode = String(config.promoCode || 'PH25').toUpperCase();
  let attemptedCart = '';
  let pending = null;

  async function requestCart(url, body) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);
    try {
      const response = await fetch(url, {
        method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: body ? JSON.stringify(body) : undefined, signal: controller.signal
      });
      if (!response.ok) throw new Error('Regional cart update unavailable');
      return await response.json();
    } finally { clearTimeout(timeout); }
  }

  function eligible(item) {
    return Number(item.product_id) === productId && Number(item.quantity) > 0 && !item.selling_plan_allocation;
  }

  function hasOtherDiscount(cart) {
    const applications = (cart.cart_level_discount_applications || []).concat(
      (cart.items || []).flatMap(item => (item.line_level_discount_allocations || [])
        .map(allocation => allocation.discount_application || {}))
    );
    return (cart.discount_codes || []).some(entry => String(entry.code || '').toUpperCase() !== promoCode) ||
      applications.some(entry => String(entry.title || '').toUpperCase() !== promoCode);
  }

  function applied(cart) {
    return (cart.discount_codes || []).some(entry => String(entry.code || '').toUpperCase() === promoCode && entry.applicable) ||
      (cart.items || []).some(item => (item.line_level_discount_allocations || [])
        .some(entry => String(entry.discount_application?.title || '').toUpperCase() === promoCode));
  }

  function targetForm() {
    return [...document.querySelectorAll('product-form form')].find(form =>
      Number(form.querySelector('[name="product-id"]')?.value) === productId);
  }

  function clearNotice() {
    document.querySelectorAll('[data-regional-promo-notice]').forEach(element => element.remove());
  }

  function showFailure() {
    const form = targetForm();
    if (!form || !config.failureMessage || form.querySelector('[data-regional-promo-notice]')) return;
    const notice = document.createElement('p');
    notice.dataset.regionalPromoNotice = '';
    notice.setAttribute('role', 'status');
    notice.className = 'caption-large';
    notice.textContent = config.failureMessage;
    form.appendChild(notice);
  }

  async function ensurePromo(renderRequest, knownEligible) {
    if (pending) return pending;
    let relevant = knownEligible === true;
    pending = (async () => {
      const cart = await requestCart(config.cartUrl);
      relevant = (cart.items || []).some(eligible);
      if (!relevant || hasOtherDiscount(cart) || applied(cart)) {
        clearNotice();
        return null;
      }
      const fingerprint = String(cart.token || '') + '|' + cart.items.filter(eligible)
        .map(item => String(item.variant_id)).sort().join(',');
      // Only one attempt per cart composition on this page. Do not fight a
      // shopper's subsequent discount removal or replace another entered code.
      if (attemptedCart === fingerprint) return null;
      attemptedCart = fingerprint;
      const updated = await requestCart(config.cartUpdateUrl, { discount: promoCode, ...renderRequest });
      if (applied(updated)) clearNotice(); else showFailure();
      return updated;
    })().catch(() => {
      attemptedCart = '';
      if (relevant) showFailure();
      // Adding the item already succeeded. Preserve that successful result,
      // its selling plan, and normal cart navigation even if the promo fails.
      return null;
    }).finally(() => { pending = null; });
    return pending;
  }

  window.PFMRegionalOffers = {
    async afterAdd(response, formData) {
      const added = response.items || [response];
      if (!added.some(item => Number(item.product_id) === productId)) return response;
      const sections = String(formData?.get('sections') || '').split(',').filter(Boolean);
      const renderRequest = sections.length ? {
        sections: sections.slice(0, 5), sections_url: String(formData.get('sections_url') || location.pathname)
      } : {};
      const updated = await ensurePromo(renderRequest, added.some(eligible));
      if (!updated) return response;
      // Shopify may change a line key when discount allocations change. The
      // notification's fragment selector must use the new server-provided key.
      const matching = (updated.items || []).find(item => item.key === response.key) ||
        (updated.items || []).find(item => Number(item.id) === Number(response.id) &&
        JSON.stringify(item.properties || {}) === JSON.stringify(response.properties || {}) &&
        String(item.selling_plan_allocation?.selling_plan?.id || '') ===
        String(response.selling_plan_allocation?.selling_plan?.id || ''));
      return {
        ...response,
        ...(matching ? { key: matching.key } : {}),
        sections: { ...(response.sections || {}), ...Object.fromEntries(
          Object.entries(updated.sections || {}).filter(([, html]) => typeof html === 'string')
        ) }
      };
    }
  };

  function applyLandingIntent() {
    const intent = new URLSearchParams(location.search).get('pfm_purchase');
    if (!['one_time', 'subscription'].includes(intent)) return;
    let interval;
    let deadline;
    function stop() {
      clearInterval(interval); clearTimeout(deadline);
      document.removeEventListener('pointerdown', respectShopper, true);
      document.removeEventListener('keydown', respectShopper, true);
    }
    function respectShopper(event) {
      if (event.isTrusted && event.target.closest?.('.stayai-purchase-label, [name="stayai_selling_plan"], [name="purchaseType"]')) stop();
    }
    function apply() {
      const form = targetForm();
      const context = form?.closest('product-info') || form?.closest('.shopify-section');
      if (!context?.querySelector('[name="stayai_selling_plan"]')) return;
      const radio = context.querySelector(intent === 'one_time' ? '#purchaseTypeOneTime' : '#purchaseTypeSubscription');
      const label = radio && context.querySelector('label[for="' + radio.id + '"]');
      if (!radio || !label) return;
      if (!radio.checked) radio.click();
      if (!label.classList.contains('is-selected')) radio.dispatchEvent(new Event('change', { bubbles: true }));
      if (radio.checked && label.classList.contains('is-selected')) stop();
    }
    document.addEventListener('pointerdown', respectShopper, true);
    document.addEventListener('keydown', respectShopper, true);
    interval = setInterval(apply, 250); deadline = setTimeout(stop, 15000); apply();
  }

  async function updateExistingCart() {
    if (!/\/cart\/?$/.test(location.pathname)) return;
    const cartItems = document.querySelector('cart-items');
    if (!cartItems?.getSectionsToRender) return;
    const sections = cartItems.getSectionsToRender();
    const updated = await ensurePromo({ sections: sections.map(section => section.section).slice(0, 5), sections_url: location.pathname });
    if (!updated?.sections) return;
    sections.forEach(section => {
      const html = updated.sections[section.section];
      const container = document.getElementById(section.id);
      if (!html || !container) return;
      const source = new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector);
      const target = container.querySelector(section.selector) || container;
      if (source) target.innerHTML = source.innerHTML;
    });
  }

  function boot() { applyLandingIntent(); updateExistingCart(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
